import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client";
import { useContacts } from "../contexts/ContactsContext";
import ConfirmDeleteModal from './shared/ConfirmDeleteModal';

const PRESET_TAGS = ["Client", "Press", "Team", "Vendor", "Partner", "Prospect", "Media", "Investor"];
const PERSONAL_CATEGORIES = ["Healthcare", "School", "Salon and beauty", "Trades", "Clubs and activities", "Family and friends", "Other"];

const TAG_COLORS = {
  Client: { bg: "#dcfce7", text: "#166534", border: "#86efac" },
  Press: { bg: "#fdf3e8", text: "#7a4a10", border: "#FBC490" },
  Team: { bg: "#eaf6ef", text: "#1e6645", border: "#6abf85" },
  Vendor: { bg: "#f3f0fb", text: "#5b3fa0", border: "#b39ddb" },
  Partner: { bg: "#fff8ee", text: "#7a4a10", border: "#FBC490" },
  Prospect: { bg: "#fef9e7", text: "#7d6608", border: "#f9e547" },
  Media: { bg: "#fce4ec", text: "#880e4f", border: "#f48fb1" },
  Investor: { bg: "#e8fde8", text: "#1b5e20", border: "#81c784" },
};

const CONTACT_CURRENCIES = [
  { code: "", label: "None" },
  { code: "GBP", label: "GBP — British Pound (£)" },
  { code: "USD", label: "USD — US Dollar ($)" },
  { code: "EUR", label: "EUR — Euro (€)" },
  { code: "CAD", label: "CAD — Canadian Dollar (CA$)" },
  { code: "AUD", label: "AUD — Australian Dollar (A$)" },
  { code: "CHF", label: "CHF — Swiss Franc" },
  { code: "JPY", label: "JPY — Japanese Yen (¥)" },
  { code: "SEK", label: "SEK — Swedish Krona" },
  { code: "NOK", label: "NOK — Norwegian Krone" },
  { code: "DKK", label: "DKK — Danish Krone" },
  { code: "NZD", label: "NZD — New Zealand Dollar (NZ$)" },
  { code: "SGD", label: "SGD — Singapore Dollar (S$)" },
  { code: "AED", label: "AED — UAE Dirham" },
  { code: "ZAR", label: "ZAR — South African Rand (R)" },
];

function initForm(contact = null) {
  return {
    name: contact?.name ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
    company: contact?.company ?? "",
    tags: contact?.tags ?? [],
    notes: contact?.notes ?? "",
    isNew: contact?.isNew ?? false,
    source: contact?.source ?? "manual",
    defaultCurrency: contact?.defaultCurrency ?? "",
    customTag: "",
    // personal mode: first tag that matches a personal category (single-select)
    personalCategory: (contact?.tags ?? []).find(t => PERSONAL_CATEGORIES.includes(t)) ?? "",
  };
}

function TagPill({ tag, onRemove, size = "sm" }) {
  const colors = TAG_COLORS[tag] || { bg: "#f0f0f0", text: "#555", border: "#ccc" };
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.2rem",
      background: colors.bg,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      padding: size === "sm" ? "0.1rem 0.5rem" : "0.2rem 0.65rem",
      fontSize: size === "sm" ? "0.72rem" : "0.8rem",
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}>
      {tag}
      {onRemove && (
        <button
          onClick={onRemove}
          style={{ background: "none", border: "none", cursor: "pointer", color: colors.text, padding: 0, lineHeight: 1, fontSize: "0.8rem", marginLeft: 1 }}
        >×</button>
      )}
    </span>
  );
}

function Initials({ name }) {
  const parts = name.trim().split(/\s+/);
  const letters = parts.length >= 2
    ? parts[0][0] + parts[parts.length - 1][0]
    : name.slice(0, 2);
  return letters.toUpperCase();
}

function avatarColor(name) {
  const palette = ["#0C2D48", "#2a6080", "#1e6645", "#5b3fa0", "#7a4a10", "#880e4f", "#1b5e20", "#7d6608"];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
}

// ─── Contact Form Modal ───────────────────────────────────────────────────────
function ContactFormModal({ contact, onSave, onClose, availableTags, isPersonal }) {
  const formTags = availableTags || PRESET_TAGS;
  const [form, setForm] = useState(initForm(contact));
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  function toggleTag(tag) {
    setForm(f => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag],
    }));
  }

  function addCustomTag() {
    const t = form.customTag.trim();
    if (!t || form.tags.includes(t)) return;
    setForm(f => ({ ...f, tags: [...f.tags, t], customTag: "" }));
  }

  function handleSave() {
    if (!form.name.trim()) return;
    // In personal mode: store the single category as the tags array (preserving any non-category tags)
    const finalTags = isPersonal
      ? [
          ...(form.personalCategory ? [form.personalCategory] : []),
          ...form.tags.filter(t => !PERSONAL_CATEGORIES.includes(t)),
        ]
      : form.tags;
    onSave({
      id: contact?.id ?? Date.now(),
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim(),
      tags: finalTags,
      notes: form.notes.trim(),
      isNew: form.isNew,
      source: form.source,
      defaultCurrency: form.defaultCurrency || null,
      interactions: contact?.interactions ?? [],
      createdAt: contact?.createdAt ?? new Date().toISOString(),
    });
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content neutral" style={{ maxWidth: isMobileLayout ? "100%" : 480, width: isMobileLayout ? "100%" : "90vw" }}>
        <button className="close-button" onClick={onClose}>✕</button>
        <h3 style={{ marginBottom: "1.25rem" }}>
          {contact ? "Edit Contact" : "Add Contact"}
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <label style={labelStyle}>
            Name <span style={{ color: "#E96A6A" }}>*</span>
            <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Full name" style={inputStyle} />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr", gap: "0.75rem" }}>
            <label style={labelStyle}>
              Email
              <input value={form.email} onChange={e => set("email", e.target.value)} placeholder="email@example.com" style={inputStyle} />
            </label>
            <label style={labelStyle}>
              Phone
              <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+44..." style={inputStyle} />
            </label>
          </div>

          <label style={labelStyle}>
            Company
            <input value={form.company} onChange={e => set("company", e.target.value)} placeholder="Company name" style={inputStyle} />
          </label>

          <div>
            {isPersonal ? (
              <>
                <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555", marginBottom: "0.4rem" }}>Category</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                  {PERSONAL_CATEGORIES.map(cat => {
                    const active = form.personalCategory === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => set("personalCategory", active ? "" : cat)}
                        style={{
                          padding: "0.2rem 0.65rem",
                          borderRadius: 10,
                          border: `1px solid ${active ? "#0C2D48" : "#ddd"}`,
                          background: active ? "#0C2D48" : "#fff",
                          color: active ? "#fff" : "#888",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#555", marginBottom: "0.4rem" }}>Tags</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
              {formTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  style={{
                    padding: "0.2rem 0.65rem",
                    borderRadius: 10,
                    border: `1px solid ${form.tags.includes(tag) ? (TAG_COLORS[tag]?.border || "#aaa") : "#ddd"}`,
                    background: form.tags.includes(tag) ? (TAG_COLORS[tag]?.bg || "#eee") : "#fff",
                    color: form.tags.includes(tag) ? (TAG_COLORS[tag]?.text || "#333") : "#888",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
            {form.tags.filter(t => !formTags.includes(t)).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginBottom: "0.4rem" }}>
                {form.tags.filter(t => !formTags.includes(t)).map(t => (
                  <TagPill key={t} tag={t} onRemove={() => toggleTag(t)} />
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: "0.4rem", flexWrap: isMobileLayout ? "wrap" : "nowrap" }}>
              <input
                value={form.customTag}
                onChange={e => set("customTag", e.target.value)}
                onKeyDown={e => e.key === "Enter" && addCustomTag()}
                placeholder="Add custom tag..."
                style={{ ...inputStyle, fontSize: "0.85rem", padding: "0.35rem 0.6rem", minWidth: 0, flex: isMobileLayout ? "1 1 100%" : 1 }}
              />
              <button onClick={addCustomTag} style={{ ...btnSecondary, padding: "0.35rem 0.75rem", fontSize: "0.85rem", width: isMobileLayout ? "100%" : "auto" }}>
                Add
              </button>
            </div>
              </>
            )}
          </div>

          <label style={labelStyle}>
            Notes
            <textarea
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              rows={3}
              placeholder="Any notes about this contact..."
              style={{ ...inputStyle, resize: "vertical" }}
            />
          </label>

          {!isPersonal && (
            <>
              <label style={labelStyle}>
                Default invoice currency
                <select
                  value={form.defaultCurrency}
                  onChange={e => set("defaultCurrency", e.target.value)}
                  style={{ ...inputStyle, fontFamily: "Raleway, sans-serif" }}
                >
                  {CONTACT_CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label style={{ display: "flex", alignItems: isMobileLayout ? "flex-start" : "center", gap: "0.5rem", fontSize: "0.88rem", color: "#555", cursor: "pointer", flexWrap: "wrap" }}>
                <input
                  type="checkbox"
                  checked={form.isNew}
                  onChange={e => set("isNew", e.target.checked)}
                  style={{ width: 15, height: 15 }}
                />
                Mark as <strong>new contact</strong>
                <span style={{ fontSize: "0.75rem", color: "#999", flexBasis: isMobileLayout ? "100%" : "auto" }}>(e.g. identified from email)</span>
              </label>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", flexDirection: isMobileLayout ? "column" : "row" }}>
          <button onClick={handleSave} style={{ ...btnPrimary, width: isMobileLayout ? "100%" : "auto" }} disabled={!form.name.trim()}>
            {contact ? "Save Changes" : "Add Contact"}
          </button>
          <button onClick={onClose} style={{ ...btnGhost, width: isMobileLayout ? "100%" : "auto" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Contact Detail Modal ─────────────────────────────────────────────────────
function ContactDetailModal({ contact, onEdit, onDelete, onMarkExisting, onClose }) {
  const [portalStatus, setPortalStatus] = useState(null); // null | 'loading' | 'invited' | 'error'
  // Block 179: notes linked to this contact (e.g. meeting transcripts saved via MeetingRecorder).
  const [linkedNotes, setLinkedNotes] = useState([]);
  useEffect(() => {
    if (!contact?.id) return;
    let cancelled = false;
    api(`/api/notes?contactId=${encodeURIComponent(contact.id)}`)
      .then(res => (res.ok ? res.json() : { notes: [] }))
      .then(data => { if (!cancelled) setLinkedNotes(data.notes || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [contact?.id]);
  const [portalError, setPortalError] = useState('');

  async function handleEnablePortal() {
    setPortalStatus('loading');
    setPortalError('');
    try {
      const enableRes = await api(`/api/portal/enable/${contact.id}`, { method: 'POST' });
      if (!enableRes.ok) throw new Error('Failed to enable portal');
      const { portal } = await enableRes.json();

      if (contact.email) {
        const inviteRes = await api(`/api/portal/${portal.id}/send-link`, { method: 'POST' });
        if (!inviteRes.ok) throw new Error('Portal enabled but invite failed to send');
        setPortalStatus('invited');
      } else {
        setPortalStatus('invited');
      }
    } catch (err) {
      setPortalError(err.message || 'Something went wrong');
      setPortalStatus('error');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content neutral"
        style={{ maxWidth: 500, width: "90vw" }}
        onClick={e => e.stopPropagation()}
      >
        <button className="close-button" onClick={onClose}>✕</button>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.25rem" }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%",
            background: avatarColor(contact.name),
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 800, fontSize: "1.1rem", flexShrink: 0,
            fontFamily: "'Poppins', sans-serif",
          }}>
            <Initials name={contact.name} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <h3 style={{ margin: 0, fontSize: "1.2rem" }}>{contact.name}</h3>
              {contact.isNew && (
                <span style={{ background: "#fde8e8", color: "#A12710", border: "1px solid #A12710", borderRadius: 8, padding: "0.05rem 0.5rem", fontSize: "0.7rem", fontWeight: 700 }}>
                  NEW
                </span>
              )}
            </div>
            {contact.company && (
              <div style={{ fontSize: "0.88rem", color: "#777" }}>{contact.company}</div>
            )}
          </div>
        </div>

        {/* Info grid */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem", marginBottom: "1rem" }}>
          {contact.email && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.9rem", width: 20, textAlign: "center" }}>✉️</span>
              <a href={`mailto:${contact.email}`} style={{ fontSize: "0.9rem", color: "#0C2D48", textDecoration: "none" }}>{contact.email}</a>
            </div>
          )}
          {contact.phone && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.9rem", width: 20, textAlign: "center" }}>📞</span>
              <span style={{ fontSize: "0.9rem", color: "#333" }}>{contact.phone}</span>
            </div>
          )}
          {contact.company && (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ fontSize: "0.9rem", width: 20, textAlign: "center" }}>🏢</span>
              <span style={{ fontSize: "0.9rem", color: "#333" }}>{contact.company}</span>
            </div>
          )}
        </div>

        {/* Tags — Client always first and visually prominent */}
        {contact.tags?.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>Tags</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
              {[...contact.tags].sort((a, b) => a === 'Client' ? -1 : b === 'Client' ? 1 : 0).map(tag => <TagPill key={tag} tag={tag} size="md" />)}
            </div>
          </div>
        )}

        {/* Notes */}
        {contact.notes && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>Notes</div>
            <p style={{ margin: 0, fontSize: "0.9rem", color: "#444", lineHeight: 1.55, background: "#f9f9f9", borderRadius: 8, padding: "0.65rem 0.75rem" }}>
              {contact.notes}
            </p>
          </div>
        )}

        {/* Block 179: notes linked to this contact — meeting transcripts and any note with this
            contact attached. Renders nothing when there are none. */}
        {linkedNotes.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>Meeting notes</div>
            {linkedNotes.map(n => (
              <div key={n.id} style={{ background: "#f9f9f9", borderRadius: 8, padding: "0.6rem 0.75rem", marginBottom: "0.4rem" }}>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#333" }}>{n.title || "Untitled"}</div>
                <div style={{ fontSize: "0.72rem", color: "#999", marginTop: "0.2rem" }}>
                  {n.updated ? new Date(n.updated).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Intake answers (Block 151 / D27 #4): snapshots from booking forms, newest first.
            Renders nothing at all when there are none. */}
        {Array.isArray(contact.intakeAnswers) && contact.intakeAnswers.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.4rem" }}>Intake answers</div>
            {[...contact.intakeAnswers].reverse().map((snap, i) => (
              <div key={i} style={{ background: "#f9f9f9", borderRadius: 8, padding: "0.65rem 0.75rem", marginBottom: "0.5rem" }}>
                <div style={{ fontSize: "0.72rem", color: "#999", marginBottom: "0.35rem" }}>
                  {snap.submittedAt ? new Date(snap.submittedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""}
                </div>
                {Object.entries(snap.answers || {}).map(([label, value]) => (
                  <div key={label} style={{ fontSize: "0.9rem", color: "#444", lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 600 }}>{label}:</span> {String(value)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Meta */}
        <div style={{ fontSize: "0.75rem", color: "#bbb", borderTop: "1px solid #f0f0f0", paddingTop: "0.75rem", marginBottom: "1rem" }}>
          Added {new Date(contact.createdAt).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}
          {contact.source !== "manual" && ` · via ${contact.source}`}
        </div>

        {/* Portal activation feedback */}
        {portalStatus === 'invited' && (
          <div style={{ marginBottom: "0.75rem", fontSize: "0.82rem", color: "#166534", background: "#dcfce7", borderRadius: 6, padding: "0.45rem 0.75rem" }}>
            {contact.email ? `Portal activated and invite sent to ${contact.email}` : 'Portal activated successfully'}
          </div>
        )}
        {portalStatus === 'error' && (
          <div style={{ marginBottom: "0.75rem", fontSize: "0.82rem", color: "#A12710", background: "#fde8e8", borderRadius: 6, padding: "0.45rem 0.75rem" }}>
            {portalError}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          {contact.isNew && (
            <button onClick={onMarkExisting} style={{ ...btnSecondary, fontSize: "0.85rem", padding: "0.4rem 0.85rem" }}>
              Mark as Existing
            </button>
          )}
          <button onClick={onEdit} style={{ ...btnSecondary, fontSize: "0.85rem", padding: "0.4rem 0.85rem" }}>Edit</button>
          <button onClick={onDelete} style={{ ...btnDanger, fontSize: "0.85rem", padding: "0.4rem 0.85rem" }}>Delete</button>
          {portalStatus !== 'invited' && (
            <button
              onClick={handleEnablePortal}
              disabled={portalStatus === 'loading'}
              style={{ ...btnSecondary, fontSize: "0.85rem", padding: "0.4rem 0.85rem", opacity: portalStatus === 'loading' ? 0.6 : 1 }}
            >
              {portalStatus === 'loading' ? 'Enabling…' : contact.email ? 'Enable Portal & Send Invite' : 'Enable Portal'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function ContactsDashboard({ isPersonal = false }) {
  const { t } = useTranslation('common');
  const { contacts, setContacts } = useContacts();
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("All");
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const [customTags, setCustomTags] = useState([]);
  const [showTagManager, setShowTagManager] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newTagInput, setNewTagInput] = useState("");

  useEffect(() => {
    api('/api/users/me')
      .then(res => res.json())
      .then(data => {
        const tags = data?.user?.preferences?.crmCustomTags;
        if (Array.isArray(tags)) setCustomTags(tags);
      })
      .catch(() => { });
  }, []);

  const allTags = [...PRESET_TAGS, ...customTags.filter(t => !PRESET_TAGS.includes(t))];

  function addCustomTag() {
    const t = newTagInput.trim();
    if (!t || allTags.includes(t)) return;
    const updated = [...customTags, t];
    setCustomTags(updated);
    api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: { crmCustomTags: updated } }) })
      .catch(() => { });
    setNewTagInput("");
  }

  function removeCustomTag(tag) {
    const updated = customTags.filter(t => t !== tag);
    setCustomTags(updated);
    api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: { crmCustomTags: updated } }) })
      .catch(() => { });
    if (tagFilter === tag) setTagFilter("All");
  }

  const tagCounts = useMemo(() => {
    const counts = {};
    contacts.forEach(c => (c.tags || []).forEach(t => { counts[t] = (counts[t] || 0) + 1; }));
    return counts;
  }, [contacts]);

  const newCount = contacts.filter(c => c.isNew).length;

  const filtered = useMemo(() => {
    return contacts.filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q
        || c.name?.toLowerCase().includes(q)
        || c.email?.toLowerCase().includes(q)
        || c.company?.toLowerCase().includes(q)
        || (c.tags || []).some(t => t.toLowerCase().includes(q));
      const matchTag = tagFilter === "All"
        ? true
        : tagFilter === "New"
          ? c.isNew
          : (c.tags || []).includes(tagFilter);
      return matchSearch && matchTag;
    });
  }, [contacts, search, tagFilter]);

  function saveContact(contact) {
    const isNew = !contacts.find(c => c.id === contact.id);
    setContacts(prev => {
      const idx = prev.findIndex(c => c.id === contact.id);
      return idx >= 0
        ? prev.map(c => c.id === contact.id ? contact : c)
        : [contact, ...prev];
    });
    setSelectedId(contact.id);
    setShowForm(false);
    setEditingContact(null);

    if (isNew) {
      api('/api/contacts', { method: 'POST', body: JSON.stringify(contact) })
        .then(res => res.json())
        .then(data => {
          if (data.contact) {
            setContacts(prev => prev.map(c => c.id === contact.id ? data.contact : c));
            setSelectedId(data.contact.id);
          }
        })
        .catch(() => { });
    } else {
      api(`/api/contacts/${contact.id}`, { method: 'PUT', body: JSON.stringify(contact) })
        .catch(() => { });
    }
  }

  function deleteContact(id) {
    setContacts(prev => prev.filter(c => c.id !== id));
    setSelectedId(null);
    api(`/api/contacts/${id}`, { method: 'DELETE' }).catch(() => { });
  }

  function markExisting(id) {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, isNew: false } : c));
    api(`/api/contacts/${id}`, { method: 'PUT', body: JSON.stringify({ isNew: false }) }).catch(() => { });
    setSelectedId(null);
  }

  const selectedContact = contacts.find(c => c.id === selectedId) ?? null;

  return (
    <div style={{ padding: "1rem", maxWidth: 1100, margin: "0 auto" }}>
      {/* Page header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem" }}>Contacts</h2>
          <div style={{ fontSize: "0.85rem", color: "#999", marginTop: "0.15rem" }}>
            {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
            {newCount > 0 && (
              <span style={{ marginLeft: "0.6rem", background: "#fde8e8", color: "#A12710", borderRadius: 8, padding: "0.1rem 0.5rem", fontSize: "0.75rem", fontWeight: 700 }}>
                {newCount} new
              </span>
            )}
          </div>
        </div>
        <button onClick={() => { setEditingContact(null); setShowForm(true); }} style={btnPrimary}>
          + {t('contacts.add_contact')}
        </button>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.5rem", alignItems: "center" }}>
        {isPersonal ? (
          // Personal: category filter chips (preset list only, no tag manager)
          ["All", ...PERSONAL_CATEGORIES].map(cat => {
            const isActive = tagFilter === cat;
            const count = cat === "All" ? contacts.length : (tagCounts[cat] || 0);
            if (cat !== "All" && count === 0) return null;
            return (
              <button
                key={cat}
                onClick={() => setTagFilter(cat)}
                style={{
                  padding: "0.25rem 0.75rem",
                  borderRadius: 20,
                  border: isActive ? "1.5px solid #0C2D48" : "1px solid #ddd",
                  background: isActive ? "#0C2D48" : "#fff",
                  color: isActive ? "#fff" : "#555",
                  fontSize: "0.8rem",
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                }}
              >
                {cat}{count > 0 && ` (${count})`}
              </button>
            );
          })
        ) : (
          // Business: original tag filter chips
          ["All", ...allTags, "New"].map(tag => {
            const isActive = tagFilter === tag;
            const count = tag === "All" ? contacts.length : tag === "New" ? newCount : (tagCounts[tag] || 0);
            if (tag !== "All" && tag !== "New" && count === 0 && !customTags.includes(tag)) return null;
            return (
              <button
                key={tag}
                onClick={() => setTagFilter(tag)}
                style={{
                  padding: "0.25rem 0.75rem",
                  borderRadius: 20,
                  border: isActive ? "1.5px solid #0C2D48" : "1px solid #ddd",
                  background: isActive ? "#0C2D48" : "#fff",
                  color: isActive ? "#fff" : (tag === "New" ? "#A12710" : "#555"),
                  fontSize: "0.8rem",
                  fontWeight: isActive ? 700 : 500,
                  cursor: "pointer",
                }}
              >
                {tag}{count > 0 && ` (${count})`}
              </button>
            );
          })
        )}
        {!isPersonal && (
          <button
            onClick={() => setShowTagManager(v => !v)}
            style={{ padding: "0.25rem 0.65rem", borderRadius: 20, border: "1px dashed #aaa", background: showTagManager ? "#f0f4f8" : "none", color: "#888", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}
          >
            {showTagManager ? "Done" : "Edit tags"}
          </button>
        )}
      </div>

      {/* Tag manager — business only */}
      {!isPersonal && showTagManager && (
        <div style={{ padding: "0.75rem 1rem", marginBottom: "0.75rem", background: "#f8f9fb", border: "1px solid #e0e0e0", borderRadius: 10 }}>
          <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.5rem" }}>
            <input
              value={newTagInput}
              onChange={e => setNewTagInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addCustomTag()}
              placeholder="New tag name…"
              style={{ flex: 1, padding: "0.35rem 0.6rem", borderRadius: 6, border: "1px solid #ccc", fontSize: "0.85rem" }}
            />
            <button
              onClick={addCustomTag}
              style={{ padding: "0.35rem 0.75rem", borderRadius: 6, border: "none", background: "#0C2D48", color: "#fff", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer" }}
            >
              Add
            </button>
          </div>
          {customTags.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
              {customTags.map(tag => (
                <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: "#fff", border: "1px solid #ccc", borderRadius: 10, padding: "0.15rem 0.55rem", fontSize: "0.78rem", fontWeight: 600, color: "#555" }}>
                  {tag}
                  <button onClick={() => removeCustomTag(tag)} style={{ background: "none", border: "none", cursor: "pointer", color: "#E96A6A", padding: 0, lineHeight: 1, fontSize: "0.85rem", marginLeft: 2 }}>×</button>
                </span>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: "0.82rem", color: "#aaa", fontStyle: "italic" }}>No custom tags yet.</p>
          )}
        </div>
      )}

      {/* Search bar */}
      <div style={{ marginBottom: "1.25rem" }}>
        <input
          type="text"
          placeholder="Search by name, email, company or tag..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "0.6rem 0.85rem",
            borderRadius: 8,
            border: "1px solid #ddd",
            fontSize: "0.95rem",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Contact card grid */}
      {filtered.length === 0 ? (
        <p style={{ color: "#aaa", textAlign: "center", padding: "3rem 0", fontSize: "0.95rem" }}>
          {contacts.length === 0 ? t('contacts.no_contacts') : t('empty.no_results')}
        </p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0.85rem" }}>
          {filtered.map(contact => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onClick={() => setSelectedId(contact.id)}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          onEdit={() => { setEditingContact(selectedContact); setSelectedId(null); setShowForm(true); }}
          onDelete={() => setDeleteConfirm({ id: selectedContact.id, name: selectedContact.name })}
          onMarkExisting={() => markExisting(selectedContact.id)}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <ContactFormModal
          contact={editingContact}
          onSave={saveContact}
          onClose={() => { setShowForm(false); setEditingContact(null); }}
          availableTags={allTags}
          isPersonal={isPersonal}
        />
      )}

      <ConfirmDeleteModal
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteContact(deleteConfirm.id)}
        itemType="contact"
        itemName={deleteConfirm?.name || ''}
        warningText="This will permanently delete this contact and all their data."
      />
    </div>
  );
}

// ─── Contact Card ─────────────────────────────────────────────────────────────
function ContactCard({ contact, onClick }) {
  const [hovered, setHovered] = useState(false);
  const bg = avatarColor(contact.name);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        border: hovered ? "1.5px solid #0C2D48" : "1px solid #e0e0e0",
        borderRadius: 12,
        padding: "1rem 1.1rem",
        cursor: "pointer",
        boxShadow: hovered ? "0 4px 16px rgba(12,45,72,0.10)" : "0 1px 4px rgba(0,0,0,0.04)",
        transition: "border-color 0.15s, box-shadow 0.15s",
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem",
      }}
    >
      {/* Avatar + name */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%", background: bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 800, fontSize: "0.9rem", flexShrink: 0,
          fontFamily: "'Poppins', sans-serif",
        }}>
          <Initials name={contact.name} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a1a" }}>{contact.name}</span>
            {contact.isNew && (
              <span style={{ background: "#fde8e8", color: "#A12710", border: "1px solid #A12710", borderRadius: 6, padding: "0 0.35rem", fontSize: "0.63rem", fontWeight: 700 }}>NEW</span>
            )}
          </div>
          {contact.company && (
            <div style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.05rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {contact.company}
            </div>
          )}
        </div>
      </div>

      {/* Contact info */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
        {contact.email && (
          <div style={{ fontSize: "0.8rem", color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ✉️ {contact.email}
          </div>
        )}
        {contact.phone && (
          <div style={{ fontSize: "0.8rem", color: "#555" }}>📞 {contact.phone}</div>
        )}
      </div>

      {/* Tags — Client always first */}
      {contact.tags?.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
          {[...contact.tags].sort((a, b) => a === 'Client' ? -1 : b === 'Client' ? 1 : 0).slice(0, 3).map(tag => <TagPill key={tag} tag={tag} />)}
          {contact.tags.length > 3 && (
            <span style={{ fontSize: "0.72rem", color: "#aaa", alignSelf: "center" }}>+{contact.tags.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = {
  display: "block",
  width: "100%",
  marginTop: "0.3rem",
  padding: "0.55rem 0.75rem",
  borderRadius: 6,
  border: "1px solid #ddd",
  fontSize: "0.95rem",
  boxSizing: "border-box",
  fontFamily: "Raleway, sans-serif",
};

const labelStyle = {
  display: "block",
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "#555",
};

const btnPrimary = {
  padding: "0.55rem 1.2rem",
  background: "#E4E69B",
  border: "none",
  borderRadius: 7,
  fontWeight: 700,
  fontSize: "0.9rem",
  cursor: "pointer",
  fontFamily: "Raleway, sans-serif",
};

const btnSecondary = {
  padding: "0.55rem 1.2rem",
  background: "#f3f3f3",
  border: "1px solid #ddd",
  borderRadius: 7,
  fontWeight: 600,
  fontSize: "0.9rem",
  cursor: "pointer",
  fontFamily: "Raleway, sans-serif",
  color: "#333",
};

const btnDanger = {
  padding: "0.55rem 1.2rem",
  background: "#E96A6A",
  border: "none",
  borderRadius: 7,
  fontWeight: 600,
  fontSize: "0.9rem",
  cursor: "pointer",
  fontFamily: "Raleway, sans-serif",
  color: "#fff",
};

const btnGhost = {
  padding: "0.55rem 1.2rem",
  background: "transparent",
  border: "1px solid #ddd",
  borderRadius: 7,
  fontWeight: 500,
  fontSize: "0.9rem",
  cursor: "pointer",
  fontFamily: "Raleway, sans-serif",
  color: "#888",
};

export default ContactsDashboard;
